import React from 'react';
import { Helmet } from 'react-helmet-async';

// Organization Schema
export const OrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "DriveMaster",
    "alternateName": ["DriveMaster Sverige", "DriveMaster Sweden", "درايف ماستر"],
    "url": "https://drivemaster.se",
    "logo": "https://drivemaster.se/logo192.png",
    "description": "Online driving theory education platform for Swedish driving license in 9 languages",
    "foundingDate": "2024",
    "sameAs": [
      "https://www.facebook.com/drivemastersweden",
      "https://www.instagram.com/drivemastersweden",
      "https://www.tiktok.com/@drivemastersweden"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": "drivemaster.se@gmail.com",
      "availableLanguage": ["Swedish", "English", "Arabic", "Turkish", "Persian", "Somali", "Polish", "Spanish", "German"]
    },
    "address": { "@type": "PostalAddress", "addressCountry": "SE", "addressLocality": "Sweden" }
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

// WebSite Schema
export const WebSiteSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "DriveMaster",
    "alternateName": "DriveMaster Sverige",
    "url": "https://drivemaster.se",
    "description": "Best platform for Swedish driving theory test preparation in 9 languages",
    "inLanguage": ["sv", "ar", "en", "tr", "fa", "so", "pl", "es", "de"],
    "potentialAction": {
      "@type": "SearchAction",
      "target": { "@type": "EntryPoint", "urlTemplate": "https://drivemaster.se/teoriprov?q={search_term_string}" },
      "query-input": "required name=search_term_string"
    }
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

// Course Schema
export const CourseSchema = ({ language = 'sv' }) => {
  const names = {
    sv: "Körkortsteori - Komplett Kurs för B-körkort",
    ar: "دورة التيوري الكاملة - رخصة القيادة السويدية B",
    en: "Swedish Driving Theory - Complete B License Course",
    tr: "İsveç Ehliyet Teorisi - Tam B Sınıfı Kursu",
    fa: "دوره کامل تئوری رانندگی سوئد - گواهینامه B",
    so: "Aragti Wadista Iswiidhan - Koorsada B",
    pl: "Teoria Prawa Jazdy Szwecja - Pełny Kurs B",
    es: "Teoría de Conducción Suecia - Curso Completo B",
    de: "Schwedische Fahrtheorie - Kompletter B-Kurs"
  };
  const descs = {
    sv: "Komplett körkortsteori med 800+ B-körkort-frågor och 480+ Taxi-frågor, 33 övningsprov, 26 lektioner, 359 trafikmärken och AI-assistent på 9 språk.",
    ar: "دورة تيوري كاملة مع 800+ سؤال لرخصة B و452 سؤال تاكسي، 33 امتحان، 26 درس، 359 إشارة مرور ومساعد ذكي بـ 9 لغات.",
    en: "Complete driving theory with 800+ B-license questions and 480+ Taxi questions, 33 practice tests, 26 lessons, 359 traffic signs and AI assistant in 9 languages."
  };
  const schema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": names[language] || names.sv,
    "description": descs[language] || descs.sv,
    "image": "https://drivemaster.se/logo512.png",
    "provider": { "@type": "Organization", "name": "DriveMaster", "url": "https://drivemaster.se" },
    "educationalLevel": "Beginner",
    "teaches": ["Swedish traffic rules", "Road signs", "Traffic safety", "Driving theory", "Speed limits", "Right of way"],
    "availableLanguage": ["sv", "ar", "en", "tr", "fa", "so", "pl", "es", "de"],
    "numberOfCredits": "0",
    "hasCourseInstance": { "@type": "CourseInstance", "courseMode": "online", "courseWorkload": "PT20H" },
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "SEK", "availability": "https://schema.org/InStock", "url": "https://drivemaster.se/priser", "description": "Start free" },
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "1250", "bestRating": "5" }
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

// FAQ Schema - ALL 9 LANGUAGES + page-specific
export const FAQSchema = ({ language = 'sv', page = 'home' }) => {
  const allFaqs = {
    home: {
      sv: [
        { question: "Är DriveMaster gratis att använda?", answer: "Ja! DriveMaster har en helt gratis del med övningsfrågor, det första övningsprovet och tillgång till alla 359 trafikmärken. För full tillgång till alla 800+ frågor och 33 prov finns Premium från 249 kr/månad." },
        { question: "Hur mycket kostar DriveMaster?", answer: "Du börjar gratis! Premium kostar från 249 kr/månad med full tillgång till allt innehåll." },
        { question: "Kan jag prova gratis?", answer: "Ja! Du får gratis övningsfrågor utan att behöva betala. Perfekt för att testa plattformen." },
        { question: "Hur många frågor har teoriprovet?", answer: "Teoriprovet hos Trafikverket har 70 frågor, varav 65 räknas (5 är testfrågor). Våra övningsprov har 30 frågor per test." },
        { question: "Är frågorna bra förberedelse inför kunskapsprov?", answer: "Våra frågor är utformade för att hjälpa dig träna inför kunskapsprov – med varierat format, svårighetsgrad och ämnen som är relevanta för dig." },
        { question: "Hur förbereder jag mig bäst för teoriprovet?", answer: "Gör övningsprov regelbundet, lär dig alla trafikmärken, studera teorilektionerna och använd vår AI-assistent för att ställa frågor om det du inte förstår." },
        { question: "Hur lång tid tar det att klara teoriprovet?", answer: "Med daglig övning kan de flesta klara teoriprovet inom 2-4 veckor. Gör minst 2-3 övningsprov per dag för bästa resultat." },
        { question: "Fungerar DriveMaster på mobilen?", answer: "Ja! DriveMaster är helt responsivt och fungerar perfekt på mobiler, surfplattor och datorer. Öva var du vill!" },
        { question: "Vad krävs för att bli godkänd på kunskapsprovet?", answer: "Du behöver minst 80% rätt (52 av 65 frågor) för att bli godkänd på Trafikverkets kunskapsprov." },
        { question: "Var kan jag ladda ner DriveMaster?", answer: "DriveMaster är en webbapp som fungerar direkt i din webbläsare på drivemaster.se. Du behöver inte ladda ner något! Öppna bara drivemaster.se på din mobil eller dator och börja öva direkt. Du kan även lägga till den på hemskärmen som en app." },
        { question: "Finns DriveMaster tillgängligt för Android?", answer: "Ja! DriveMaster fungerar på alla Android-enheter direkt i webbläsaren. Gå till drivemaster.se på din Android-telefon. Du kan också lägga till den på hemskärmen: tryck på menyn i Chrome och välj 'Lägg till på startskärmen' för en app-liknande upplevelse." },
        { question: "Finns DriveMaster tillgängligt för iPhone?", answer: "Ja! DriveMaster fungerar perfekt på iPhone och iPad via Safari eller Chrome. Gå till drivemaster.se och tryck på dela-knappen och sedan 'Lägg till på hemskärmen' för att använda den som en app." },
        { question: "Vilka språk stöds av DriveMaster?", answer: "DriveMaster stöder 9 språk: svenska, arabiska, engelska, turkiska, persiska, somaliska, polska, spanska och tyska. Hela plattformen, alla frågor och förklaringar är översatta." },
        { question: "Vad är DriveMaster?", answer: "DriveMaster (drivemaster.se) är Sveriges mest kompletta plattform för körkortsteoriprov. Med 800+ frågor, 33 övningsprov, 359 trafikmärken, 26 lektioner och AI-assistent på 9 språk hjälper vi dig klara teoriprovet på första försöket." },
        { question: "Är DriveMaster gratis?", answer: "DriveMaster har en gratis version med övningsfrågor och det första övningsprovet. För full tillgång till alla 800+ frågor och 33 prov finns Premium från 249 kr/månad." }
      ],
      ar: [
        { question: "كم تكلفة الاشتراك؟", answer: "ابدأ مجاناً! الاشتراك المميز من 249 كرونة/شهر مع وصول كامل لكل المحتوى." },
        { question: "هل يمكنني التجربة مجاناً؟", answer: "نعم! تحصل على أسئلة تدريبي مجاني بدون دفع أي مبلغ." },
        { question: "كم عدد أسئلة امتحان التيوري؟", answer: "امتحان التيوري في Trafikverket يحتوي على 70 سؤالاً، يُحسب منها 65 (5 أسئلة تجريبية). اختباراتنا التدريبية بـ 30 سؤال لكل اختبار." },
        { question: "هل الأسئلة مفيدة للتحضير للاختبار النظري؟", answer: "نعم! أسئلتنا مصممة لمساعدتك على التحضير لاختبار المعرفة النظرية من حيث الشكل والصعوبة والمواضيع." },
        { question: "كيف أحضّر نفسي لامتحان التيوري؟", answer: "حل اختبارات تجريبية يومياً، تعلم كل إشارات المرور، ادرس الدروس النظرية واستخدم المساعد الذكي للاستفسار عن أي شيء لا تفهمه." },
        { question: "كم يستغرق التحضير لامتحان التيوري؟", answer: "مع التدريب اليومي، معظم الطلاب يجتازون الامتحان خلال 2-4 أسابيع. حل على الأقل 2-3 اختبارات يومياً للحصول على أفضل نتيجة." },
        { question: "هل يعمل DriveMaster على الجوال؟", answer: "نعم! DriveMaster يعمل بشكل ممتاز على الجوال والتابلت والكمبيوتر. تدرب في أي مكان وأي وقت!" },
        { question: "ما هي نسبة النجاح المطلوبة؟", answer: "تحتاج 80% على الأقل (52 من 65 سؤال) للنجاح في امتحان Trafikverket الرسمي." },
        { question: "هل يوجد شرح للإجابات؟", answer: "نعم! كل سؤال عنده شرح مفصل للإجابة الصحيحة عشان تفهم السبب وما تغلط مرة ثانية." },
        { question: "أين أحمّل DriveMaster؟", answer: "DriveMaster تطبيق ويب يعمل مباشرة من المتصفح على drivemaster.se. لا تحتاج تحميل أي شيء! افتح drivemaster.se على جوالك أو كمبيوترك وابدأ التدريب مباشرة. تقدر تضيفه للشاشة الرئيسية كتطبيق." },
        { question: "هل DriveMaster متاح لأندرويد؟", answer: "نعم! DriveMaster يعمل على كل أجهزة أندرويد مباشرة من المتصفح. افتح drivemaster.se من Chrome وأضفه للشاشة الرئيسية ليعمل كتطبيق." },
        { question: "هل DriveMaster متاح للآيفون؟", answer: "نعم! DriveMaster يعمل بشكل ممتاز على آيفون وآيباد. افتح drivemaster.se من Safari واضغط زر المشاركة ثم 'إضافة إلى الشاشة الرئيسية'." },
        { question: "ما هو DriveMaster؟", answer: "DriveMaster (drivemaster.se) هو أشمل منصة في السويد لاختبارات رخصة القيادة النظرية. مع 800+ سؤال، 33 امتحان تجريبي، 359 إشارة مرور، 26 درس ومساعد ذكي بـ 9 لغات." },
        { question: "هل DriveMaster مجاني؟", answer: "DriveMaster فيه نسخة مجانية مع أسئلة والامتحان الأول مجاناً. للوصول الكامل لكل 800+ سؤال و24 امتحان، الاشتراك المميز من 249 كرونة/شهر." }
      ],
      en: [
        { question: "How much does it cost?", answer: "Start free! Premium from 249 SEK/month with full access to all content." },
        { question: "Can I try for free?", answer: "Yes! You get free practice questions without any payment required." },
        { question: "How many questions are on the real test?", answer: "The Trafikverket theory test has 70 questions, of which 65 are scored (5 are trial questions). Our practice tests use a 50-minute time limit." },
        { question: "Are the questions good preparation for the theory test?", answer: "Our questions are designed to help you practise for the theory test, covering relevant formats, difficulty levels, and topics." },
        { question: "How long does it take to prepare?", answer: "With daily practice, most students pass within 2-4 weeks. Complete at least 2-3 practice tests per day for best results." },
        { question: "Does DriveMaster work on mobile?", answer: "Yes! DriveMaster is fully responsive and works perfectly on phones, tablets and computers. Study anywhere!" },
        { question: "What passing score is required?", answer: "You need at least 80% correct (52 of 65 questions) to pass Trafikverket's official theory test." },
        { question: "Do you explain the answers?", answer: "Yes! Every question has a detailed explanation so you understand why the correct answer is right." },
        { question: "Where can I download DriveMaster?", answer: "DriveMaster is a web app that works directly in your browser at drivemaster.se. No download needed! Just open drivemaster.se on your phone or computer and start practicing. You can also add it to your home screen as an app." },
        { question: "Is DriveMaster available for Android?", answer: "Yes! DriveMaster works on all Android devices directly in the browser. Go to drivemaster.se on Chrome and add it to your home screen for an app-like experience." },
        { question: "Is DriveMaster available for iPhone?", answer: "Yes! DriveMaster works perfectly on iPhone and iPad. Go to drivemaster.se in Safari, tap the share button and select 'Add to Home Screen'." },
        { question: "What is DriveMaster?", answer: "DriveMaster (drivemaster.se) is Sweden's most complete platform for driving theory tests. With 800+ questions, 33 practice tests, 359 traffic signs, 26 lessons and AI assistant in 9 languages." },
        { question: "Is DriveMaster free?", answer: "DriveMaster has a free version with questions and the first practice test. For full access to all 800+ questions and 33 tests, Premium starts at 249 SEK/month." }
      ],
      tr: [
        { question: "DriveMaster ücretsiz mi?", answer: "Evet! DriveMaster'ın soru, ilk deneme sınavı ve tüm 359 trafik işaretine erişim içeren tamamen ücretsiz bir bölümü vardır. 800+ soru ve 33 testin tamamına erişim için Premium ayda 249 SEK'den başlar." },
        { question: "DriveMaster nasıl çalışır?", answer: "DriveMaster, 800+ soru, 33 deneme sınavı, 26 ders ve AI asistanla çevrimiçi ehliyet eğitim platformudur." },
        { question: "Hangi diller mevcut?", answer: "9 dilde: İsveççe, Arapça, İngilizce, Türkçe, Farsça, Somalice, Lehçe, İspanyolca ve Almanca." },
        { question: "Ücretsiz deneyebilir miyim?", answer: "Evet! Ödeme yapmadan ücretsiz soru alırsınız." },
        { question: "Sınavı geçmek için kaç doğru gerekli?", answer: "Trafikverket sınavını geçmek için en az %80 (65 sorudan 52) doğru yanıt gerekir." },
        { question: "Hazırlık ne kadar sürer?", answer: "Günlük pratikle çoğu öğrenci 2-4 haftada geçer. Günde en az 2-3 deneme sınavı çözün." },
        { question: "Mobilde çalışır mı?", answer: "Evet! DriveMaster telefon, tablet ve bilgisayarda mükemmel çalışır." },
        { question: "DriveMaster'ı nereden indirebilirim?", answer: "DriveMaster, drivemaster.se adresinden doğrudan tarayıcınızda çalışan bir web uygulamasıdır. İndirmeye gerek yok!" },
        { question: "DriveMaster Android için mevcut mu?", answer: "Evet! DriveMaster tüm Android cihazlarda tarayıcı üzerinden çalışır. drivemaster.se'yi açın ve ana ekrana ekleyin." },
        { question: "Cevap açıklamaları var mı?", answer: "Evet! Her sorunun doğru cevabın neden doğru olduğunu açıklayan detaylı bir açıklaması vardır." },
        { question: "DriveMaster nedir?", answer: "DriveMaster (drivemaster.se) İsveç'in en kapsamlı ehliyet teorisi platformudur. 800+ soru, 33 deneme sınavı, 359 trafik işareti, 26 ders ve 9 dilde AI asistan." }
      ],
      fa: [
        { question: "آیا استفاده از DriveMaster رایگان است؟", answer: "بله! DriveMaster بخش رایگانی با سوال‌های تمرینی، اولین آزمون و دسترسی به تمام 359 تابلوی راهنمایی دارد. برای دسترسی کامل به 800+ سوال و 33 آزمون، اشتراک Premium از 199 کرون در ماه شروع می‌شود." },
        { question: "DriveMaster چگونه کار می‌کند؟", answer: "DriveMaster یک پلتفرم آموزش رانندگی آنلاین با 800+ سوال، 33 آزمون و دستیار هوشمند است." },
        { question: "چه زبان‌هایی موجود است؟", answer: "9 زبان: سوئدی، عربی، انگلیسی، ترکی، فارسی، سومالیایی، لهستانی، اسپانیایی و آلمانی." },
        { question: "آیا می‌توانم رایگان امتحان کنم؟", answer: "بله! سوال‌های رایگان بدون نیاز به پرداخت." },
        { question: "برای قبولی چند درصد لازم است؟", answer: "حداقل 80% (52 از 65 سوال) برای آمادگی کامل جهت قبولی در کنسkapsprov Trafikverket." },
        { question: "آمادگی چقدر طول می‌کشد؟", answer: "با تمرین روزانه، اکثر دانش‌آموزان ظرف 2-4 هفته قبول می‌شوند." },
        { question: "آیا روی موبایل کار می‌کند؟", answer: "بله! DriveMaster روی موبایل، تبلت و کامپیوتر کاملاً کار می‌کند. هرجا که هستید تمرین کنید." },
        { question: "DriveMaster را از کجا دانلود کنم؟", answer: "DriveMaster یک وب‌اپ است که مستقیماً در مرورگر شما در drivemaster.se کار می‌کند. نیازی به دانلود نیست!" },
        { question: "آیا برای اندروید موجود است؟", answer: "بله! DriveMaster روی همه دستگاه‌های اندروید از طریق مرورگر کار می‌کند. drivemaster.se را در Chrome باز کنید." },
        { question: "آیا برای آیفون موجود است؟", answer: "بله! DriveMaster روی آیفون و آیپد در Safari عالی کار می‌کند. می‌توانید آن را به صفحه اصلی اضافه کنید." },
        { question: "آیا توضیحات پاسخ‌ها داده می‌شود؟", answer: "بله! هر سوال توضیح کاملی دارد تا دلیل پاسخ صحیح را بفهمید." },
        { question: "DriveMaster چیست؟", answer: "DriveMaster (drivemaster.se) کامل‌ترین پلتفرم آزمون تئوری رانندگی سوئد با 800+ سوال به 9 زبان است." }
      ],
      so: [
        { question: "DriveMaster ma bilaash baa?", answer: "Haa! DriveMaster wuxuu leeyahay qayb bilaash ah oo leh su'aalood, imtixaanka koowaad iyo helitaanka 359 calaamadood. Helitaan buuxa oo dhammaan 800+ su'aalood iyo 33 imtixaan, Premium wuxuu ka bilaabmaa 249 SEK/bishii." },
        { question: "Sidee DriveMaster u shaqeeyaa?", answer: "DriveMaster waa goob waxbarasho darawalnimo oo leh 800+ su'aal, 33 imtixaan iyo kaaliye AI." },
        { question: "Ma bilaash tijaabi karaa?", answer: "Haa! su'aalo bilaash si aad u tijaabiso barnaamijka." },
        { question: "Imisa su'aal ayaa imtixaanka dhabta ah leedahay?", answer: "Imtixaanka Trafikverket wuxuu leeyahay 70 su'aal, 65 ka mid ah ayaa la xisaabiyaa. 80% ayaad u baahan tahay si aad u gudbid." },
        { question: "Diyaarinta intee ayey qaadataa?", answer: "Tababar maalinle ah, 2-4 toddobaad gudahood ayaad gudbi kartaa." },
        { question: "Luuqadaha kuwee la taageeraa?", answer: "9 luuqado: Iswiidish, Carabi, Ingiriis, Turki, Faaris, Soomaali, Boolish, Isbaanish iyo Jarmal." },
        { question: "Ma ku shaqeeyaa moobiilka?", answer: "Haa! DriveMaster wuxuu si fiican ugu shaqeeyaa moobiilada, taabalada iyo computerada." },
        { question: "Xaggee ka soo degsan karaa DriveMaster?", answer: "DriveMaster waa web app oo ku shaqeeya browser-kaaga drivemaster.se. Looma baahna inaad wax soo degdegto!" },
        { question: "Ma jiraan sharaxaadyo jawaabaha?", answer: "Haa! Su'aal kasta waxay leedahay sharaxaad faahfaahsan." },
        { question: "Waa maxay DriveMaster?", answer: "DriveMaster (drivemaster.se) waa goobta ugu dhammaystiran ee imtixaanka darawalnimada Iswiidhan 9 luuqadood." }
      ],
      pl: [
        { question: "Czy DriveMaster jest darmowy?", answer: "Tak! DriveMaster ma w pełni darmową część z pytaniami, pierwszym testem i dostępem do wszystkich 359 znaków drogowych. Premium z dostępem do 800+ pytań i 33 testów zaczyna się od 249 SEK/miesiąc." },
        { question: "Jak działa DriveMaster?", answer: "DriveMaster to platforma z 800+ pytaniami, 33 testami i asystentem AI w 9 językach." },
        { question: "Czy mogę spróbować za darmo?", answer: "Tak! pytania za darmo, bez konieczności płacenia." },
        { question: "Ile procent potrzeba do zdania?", answer: "Minimum 80% (52 z 65 pytań) na egzaminie Trafikverket." },
        { question: "Ile trwa przygotowanie?", answer: "Przy codziennej nauce, większość zdaje w 2-4 tygodnie." },
        { question: "Jakie języki są dostępne?", answer: "9 języków: szwedzki, arabski, angielski, turecki, perski, somalijski, polski, hiszpański i niemiecki." },
        { question: "Czy działa na telefonie?", answer: "Tak! DriveMaster działa doskonale na telefonach, tabletach i komputerach." },
        { question: "Skąd pobrać DriveMaster?", answer: "DriveMaster to aplikacja webowa działająca w przeglądarce na drivemaster.se. Nie trzeba nic pobierać!" },
        { question: "Czy są wyjaśnienia odpowiedzi?", answer: "Tak! Każde pytanie ma szczegółowe wyjaśnienie poprawnej odpowiedzi." },
        { question: "Czym jest DriveMaster?", answer: "DriveMaster (drivemaster.se) to najlepsza platforma do nauki teorii prawa jazdy w Szwecji w 9 językach." }
      ],
      es: [
        { question: "¿Es DriveMaster gratis?", answer: "¡Sí! DriveMaster tiene una parte completamente gratis con preguntas, el primer examen y acceso a las 359 señales de tráfico. Premium con acceso completo a 800+ preguntas y 33 tests desde 249 SEK/mes." },
        { question: "¿Cómo funciona DriveMaster?", answer: "DriveMaster es una plataforma con 800+ preguntas, 33 tests y asistente IA en 9 idiomas." },
        { question: "¿Puedo probar gratis?", answer: "¡Sí! preguntas gratis sin necesidad de pagar." },
        { question: "¿Qué porcentaje se necesita para aprobar?", answer: "Mínimo 80% (52 de 65 preguntas) para estar bien preparado para el kunskapsprov de Trafikverket." },
        { question: "¿Cuánto tiempo toma prepararse?", answer: "Con práctica diaria, la mayoría aprueba en 2-4 semanas." },
        { question: "¿Qué idiomas están disponibles?", answer: "9 idiomas: sueco, árabe, inglés, turco, persa, somalí, polaco, español y alemán." },
        { question: "¿Funciona en el móvil?", answer: "¡Sí! DriveMaster funciona perfectamente en móviles, tablets y ordenadores." },
        { question: "¿Dónde puedo descargar DriveMaster?", answer: "DriveMaster es una app web que funciona en tu navegador en drivemaster.se. ¡No necesitas descargar nada!" },
        { question: "¿Hay explicaciones de las respuestas?", answer: "¡Sí! Cada pregunta tiene una explicación detallada de la respuesta correcta." },
        { question: "¿Qué es DriveMaster?", answer: "DriveMaster (drivemaster.se) es la plataforma más completa de Suecia para exámenes teóricos de conducir en 9 idiomas." }
      ],
      de: [
        { question: "Ist DriveMaster kostenlos?", answer: "Ja! DriveMaster hat einen komplett kostenlosen Teil mit Fragen, dem ersten Test und Zugriff auf alle 359 Verkehrszeichen. Premium mit vollem Zugriff auf 800+ Fragen und 33 Tests ab 249 SEK/Monat." },
        { question: "Wie funktioniert DriveMaster?", answer: "DriveMaster ist eine Plattform mit 800+ Fragen, 33 Tests und KI-Assistent in 9 Sprachen." },
        { question: "Kann ich kostenlos testen?", answer: "Ja! Fragen kostenlos, ohne Bezahlung." },
        { question: "Welcher Prozentsatz ist zum Bestehen nötig?", answer: "Mindestens 80% (52 von 65 Fragen) bei der offiziellen Trafikverket-Prüfung." },
        { question: "Wie lange dauert die Vorbereitung?", answer: "Bei täglichem Üben bestehen die meisten in 2-4 Wochen." },
        { question: "Welche Sprachen sind verfügbar?", answer: "9 Sprachen: Schwedisch, Arabisch, Englisch, Türkisch, Persisch, Somali, Polnisch, Spanisch und Deutsch." },
        { question: "Funktioniert es auf dem Handy?", answer: "Ja! DriveMaster funktioniert perfekt auf Handys, Tablets und Computern." },
        { question: "Wo kann ich DriveMaster herunterladen?", answer: "DriveMaster ist eine Web-App die direkt im Browser auf drivemaster.se funktioniert. Kein Download nötig!" },
        { question: "Gibt es Erklärungen zu den Antworten?", answer: "Ja! Jede Frage hat eine detaillierte Erklärung zur richtigen Antwort." },
        { question: "Was ist DriveMaster?", answer: "DriveMaster (drivemaster.se) ist Schwedens umfassendste Plattform für Führerschein-Theorietests in 9 Sprachen." }
      ]
    },
    tests: {
      sv: [
        { question: "Hur många övningsprov finns det?", answer: "Vi har 33 övningsprov med 30 frågor vardera." },
        { question: "Hur lång tid har man på sig?", answer: "Varje övningsprov har en tidsgräns på 50 minuter, så att du tränar dig inför kunskapsprovets tempo." },
        { question: "Vad krävs för att bli godkänd?", answer: "Du behöver 80% rätt (52 av 65 frågor) för att bli godkänd, samma krav som Trafikverkets kunskapsprov." },
        { question: "Är det första övningsprovet gratis?", answer: "Ja! Teoriprov 1 är helt gratis. Du behöver inte ens registrera dig." },
        { question: "Får man se förklaring efter varje fråga?", answer: "Ja! Efter varje fråga får du en detaljerad förklaring så att du förstår varför svaret är rätt. Perfekt för inlärning." },
        { question: "Kan jag göra om samma prov?", answer: "Ja, du kan göra samma övningsprov hur många gånger du vill. Frågorna blandas varje gång för bättre inlärning." }
      ],
      ar: [
        { question: "كم عدد الاختبارات التدريبية؟", answer: "لدينا 24 اختبار تدريبي مع 30 سؤال لكل اختبار." },
        { question: "كم الوقت المتاح لكل اختبار؟", answer: "كل اختبار له حد زمني 50 دقيقة، لمساعدتك على التدرب وفق إيقاع اختبار المعرفة." },
        { question: "ما هي نسبة النجاح المطلوبة؟", answer: "تحتاج 80% (52 من 65 سؤال) للنجاح، نفس متطلبات Trafikverket." },
        { question: "هل الاختبار الأول مجاني؟", answer: "نعم! اختبار نظري 1 مجاني بالكامل بدون تسجيل." },
        { question: "هل يوجد شرح بعد كل سؤال؟", answer: "نعم! بعد كل سؤال تحصل على شرح مفصل للإجابة الصحيحة عشان تفهم السبب ولا تغلط مرة ثانية." },
        { question: "هل أقدر أعيد نفس الاختبار؟", answer: "نعم! تقدر تعيد أي اختبار كم ما تبي. الأسئلة تتغير ترتيبها كل مرة عشان تتعلم أفضل." }
      ],
      en: [
        { question: "How many practice tests are there?", answer: "We have 33 practice tests with 30 questions each." },
        { question: "How long is the time limit?", answer: "Each test has a 50-minute time limit, just like the real Trafikverket test." },
        { question: "What score is needed to pass?", answer: "You need 80% correct (52 of 65 questions), same as Trafikverket's requirement." },
        { question: "Is the first test free?", answer: "Yes! Theory Test 1 is completely free. No registration needed." },
        { question: "Are explanations provided?", answer: "Yes! After each question, you get a detailed explanation so you understand why the answer is correct." },
        { question: "Can I retake tests?", answer: "Yes! You can retake any test as many times as you want. Questions are shuffled each time for better learning." }
      ],
      tr: [
        { question: "Kaç deneme sınavı var?", answer: "Her biri 30 soruluk 33 deneme sınavı var." },
        { question: "Süre sınırı ne kadar?", answer: "Her sınavda 50 dakika süre var, kunskapsprov temposunda pratik yapmanıza yardımcı olmak için." },
        { question: "Geçme notu nedir?", answer: "%80 (65 sorudan 52) doğru yanıt gerekir." },
        { question: "İlk sınav ücretsiz mi?", answer: "Evet! Teori Sınavı 1 tamamen ücretsiz, kayıt gerekmez." }
      ],
      fa: [
        { question: "چند آزمون تمرینی وجود دارد؟", answer: "33 آزمون تمرینی با 30 سوال." },
        { question: "محدودیت زمانی چقدر است؟", answer: "هر آزمون 50 دقیقه، تا برای kunskapsprov آماده شوید." },
        { question: "اولین آزمون رایگان است؟", answer: "بله! آزمون تئوری 1 کاملاً رایگان است." }
      ],
      so: [
        { question: "Immisa imtixaan ayaa jira?", answer: "33 imtixaan oo 30 su'aal ah mid kasta." },
        { question: "Kan ugu horeysa ma bilaash baa?", answer: "Haa! Imtixaanka 1 waa bilaash." }
      ]
    },
    'traffic-signs': {
      sv: [
        { question: "Hur många trafikmärken finns i Sverige?", answer: "Vi har 359 trafikmärken uppdelade i 10 kategorier: varningsskyltar, förbudsmärken, påbudsmärken, vägvisare och mer." },
        { question: "Kan jag lära mig trafikmärken gratis?", answer: "Ja! De första 2 trafikmärkena är gratis att studera med förklaringar." }
      ],
      ar: [
        { question: "كم عدد إشارات المرور في السويد؟", answer: "لدينا 359 إشارة مرور مقسمة إلى 10 فئات: تحذير، منع، إلزام، إرشاد وأكثر." },
        { question: "هل يمكنني تعلم الإشارات مجاناً؟", answer: "نعم! أول إشارتين مجانية مع الشرح الكامل." }
      ],
      en: [
        { question: "How many traffic signs are in Sweden?", answer: "We have 359 traffic signs in 10 categories: warning, prohibition, mandatory, information and more." },
        { question: "Can I learn signs for free?", answer: "Yes! The first 2 signs are free to study with full explanations." }
      ],
      tr: [
        { question: "İsveç'te kaç trafik işareti var?", answer: "10 kategoride 359 trafik işaretimiz var: uyarı, yasak, zorunluluk, bilgi ve daha fazlası." },
        { question: "Trafik işaretlerini ücretsiz öğrenebilir miyim?", answer: "Evet! İlk 2 trafik işareti ücretsiz olarak açıklamalarıyla birlikte çalışılabilir." }
      ],
      fa: [
        { question: "چند تابلو راهنمایی در سوئد وجود دارد؟", answer: "ما 365 تابلو در 10 دسته داریم: هشدار، ممنوعیت، الزامی، اطلاعاتی و بیشتر." },
        { question: "آیا می‌توانم رایگان یاد بگیرم؟", answer: "بله! 2 تابلو اول رایگان با توضیحات کامل." }
      ],
      so: [
        { question: "Imisa calaamad taraafikada ayaa Iswiidhan ku jira?", answer: "Waxaan haynaa 359 calaamadood oo 10 qaybood ah: digniin, mamnuuc, waajib, macluumaad iyo wax kale." },
        { question: "Ma bilaash baran karaa?", answer: "Haa! 2-da calaamadood ee ugu horreeya bilaash ayay yihiin." }
      ],
      pl: [
        { question: "Ile znaków drogowych jest w Szwecji?", answer: "Mamy 359 znaków drogowych w 10 kategoriach: ostrzegawcze, zakazu, nakazu, informacyjne i więcej." },
        { question: "Czy mogę uczyć się znaków za darmo?", answer: "Tak! Pierwsze 2 znaki są darmowe z pełnymi wyjaśnieniami." }
      ],
      es: [
        { question: "¿Cuántas señales de tráfico hay en Suecia?", answer: "Tenemos 359 señales en 10 categorías: advertencia, prohibición, obligación, información y más." },
        { question: "¿Puedo aprender señales gratis?", answer: "¡Sí! Las primeras 2 señales son gratis con explicaciones completas." }
      ],
      de: [
        { question: "Wie viele Verkehrszeichen gibt es in Schweden?", answer: "Wir haben 359 Verkehrszeichen in 10 Kategorien: Warnung, Verbot, Gebot, Information und mehr." },
        { question: "Kann ich Verkehrszeichen kostenlos lernen?", answer: "Ja! Die ersten 2 Zeichen sind kostenlos mit vollständigen Erklärungen." }
      ]
    }
  };

  const pageFaqs = allFaqs[page] || allFaqs.home;
  const selectedFaqs = pageFaqs[language] || pageFaqs.sv || pageFaqs.en || [];
  if (!selectedFaqs.length) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": selectedFaqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
    }))
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

// Product Schema
export const ProductSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "DriveMaster Premium",
    "description": "Full access to Swedish driving theory course with 800+ questions, 33 practice tests, 359 traffic signs and AI assistant in 9 languages",
    "image": [
      "https://drivemaster.se/logo512.png"
    ],
    "sku": "DM-PREMIUM-2026",
    "brand": { "@type": "Brand", "name": "DriveMaster" },
    "category": "Educational Software",
    "offers": [
      { "@type": "Offer", "name": "Monthly", "price": "249", "priceCurrency": "SEK", "priceValidUntil": "2027-12-31", "availability": "https://schema.org/InStock", "url": "https://drivemaster.se/priser", "itemCondition": "https://schema.org/NewCondition",
        "shippingDetails": { "@type": "OfferShippingDetails", "shippingRate": { "@type": "MonetaryAmount", "value": "0", "currency": "SEK" }, "shippingDestination": { "@type": "DefinedRegion", "addressCountry": "SE" }, "deliveryTime": { "@type": "ShippingDeliveryTime", "handlingTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 0, "unitCode": "DAY" }, "transitTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 0, "unitCode": "DAY" } } },
        "hasMerchantReturnPolicy": { "@type": "MerchantReturnPolicy", "applicableCountry": "SE", "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow", "merchantReturnDays": 14, "returnMethod": "https://schema.org/ReturnByMail", "returnFees": "https://schema.org/FreeReturn" }
      },
      { "@type": "Offer", "name": "6 Months", "price": "699", "priceCurrency": "SEK", "priceValidUntil": "2027-12-31", "availability": "https://schema.org/InStock", "url": "https://drivemaster.se/priser", "itemCondition": "https://schema.org/NewCondition",
        "shippingDetails": { "@type": "OfferShippingDetails", "shippingRate": { "@type": "MonetaryAmount", "value": "0", "currency": "SEK" }, "shippingDestination": { "@type": "DefinedRegion", "addressCountry": "SE" }, "deliveryTime": { "@type": "ShippingDeliveryTime", "handlingTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 0, "unitCode": "DAY" }, "transitTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 0, "unitCode": "DAY" } } },
        "hasMerchantReturnPolicy": { "@type": "MerchantReturnPolicy", "applicableCountry": "SE", "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow", "merchantReturnDays": 14, "returnMethod": "https://schema.org/ReturnByMail", "returnFees": "https://schema.org/FreeReturn" }
      },
      { "@type": "Offer", "name": "3 Months", "price": "499", "priceCurrency": "SEK", "priceValidUntil": "2027-12-31", "availability": "https://schema.org/InStock", "url": "https://drivemaster.se/priser", "itemCondition": "https://schema.org/NewCondition",
        "shippingDetails": { "@type": "OfferShippingDetails", "shippingRate": { "@type": "MonetaryAmount", "value": "0", "currency": "SEK" }, "shippingDestination": { "@type": "DefinedRegion", "addressCountry": "SE" }, "deliveryTime": { "@type": "ShippingDeliveryTime", "handlingTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 0, "unitCode": "DAY" }, "transitTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 0, "unitCode": "DAY" } } },
        "hasMerchantReturnPolicy": { "@type": "MerchantReturnPolicy", "applicableCountry": "SE", "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow", "merchantReturnDays": 14, "returnMethod": "https://schema.org/ReturnByMail", "returnFees": "https://schema.org/FreeReturn" }
      }
    ],
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "1250", "bestRating": "5", "worstRating": "1" },
    "review": {
      "@type": "Review",
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
      "author": { "@type": "Person", "name": "Ahmed" },
      "reviewBody": "Excellent app for preparing for the Swedish driving theory test. Available in Arabic which made it very easy to understand."
    }
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

// BreadcrumbList Schema - LOCALIZED
export const BreadcrumbSchema = ({ items }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

// EducationalOrganization Schema
export const EducationalOrganizationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "DriveMaster",
    "url": "https://drivemaster.se",
    "description": "Online driving theory education platform for Swedish B-license in 9 languages",
    "areaServed": { "@type": "Country", "name": "Sweden" },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Driving Theory Courses",
      "itemListElement": [{ "@type": "Course", "name": "Swedish Driving Theory B-License", "description": "Complete theory course with 800+ questions" }]
    }
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

// SoftwareApplication Schema
export const SoftwareApplicationSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "DriveMaster",
    "operatingSystem": "Web",
    "applicationCategory": "EducationalApplication",
    "image": "https://drivemaster.se/logo512.png",
    "screenshot": "https://drivemaster.se/logo512.png",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "SEK" },
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "ratingCount": "1250" }
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

// Quiz/Test Schema - for tests page
export const QuizSchema = ({ language = 'sv' }) => {
  const names = {
    sv: "Övningsprov Körkort - 24 Gratis Övningsprov",
    ar: "اختبارات تيوري السويد - 33 امتحان تدريبي",
    en: "Swedish Driving Practice Tests - 24 Free Mock Exams"
  };
  const schema = {
    "@context": "https://schema.org",
    "@type": "Quiz",
    "name": names[language] || names.sv,
    "educationalAlignment": { "@type": "AlignmentObject", "alignmentType": "assesses", "targetName": "Swedish Driving Theory Knowledge" },
    "assesses": "Swedish driving theory, traffic rules, road signs",
    "numberOfQuestions": 30,
    "timeRequired": "PT50M",
    "educationalLevel": "Beginner",
    "inLanguage": language
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

// ItemList Schema - for traffic signs page
export const TrafficSignsListSchema = ({ language = 'sv', signs = [] }) => {
  const names = {
    sv: "Alla Svenska Trafikmärken - 365 Vägmärken",
    ar: "جميع إشارات المرور السويدية - 359 إشارة",
    en: "All Swedish Traffic Signs - 365 Road Signs"
  };
  
  const listItems = signs.length > 0
    ? signs.slice(0, 50).map((sign, idx) => {
        const name = typeof sign.name === 'string' ? sign.name : (sign.name?.[language] || sign.name?.sv || '');
        const item = {
          "@type": "ListItem",
          "position": idx + 1,
          "name": name,
          "url": `https://drivemaster.se/trafikmarken`
        };
        if (sign.image_url) {
          const absUrl = sign.image_url.startsWith('http') ? sign.image_url : `https://drivemaster.se${sign.image_url.startsWith('/') ? '' : '/'}${sign.image_url}`;
          item.image = absUrl;
        }
        return item;
      })
    : [
        { "@type": "ListItem", "position": 1, "name": language === 'ar' ? "إشارات التحذير" : language === 'sv' ? "Varningsskyltar" : "Warning Signs" },
        { "@type": "ListItem", "position": 2, "name": language === 'ar' ? "إشارات المنع" : language === 'sv' ? "Förbudsmärken" : "Prohibition Signs" },
        { "@type": "ListItem", "position": 3, "name": language === 'ar' ? "إشارات الإلزام" : language === 'sv' ? "Påbudsmärken" : "Mandatory Signs" },
        { "@type": "ListItem", "position": 4, "name": language === 'ar' ? "إشارات الإرشاد" : language === 'sv' ? "Upplysningsskyltar" : "Information Signs" },
        { "@type": "ListItem", "position": 5, "name": language === 'ar' ? "إشارات الأولوية" : language === 'sv' ? "Väjningsplikt & förkörsrätt" : "Priority Signs" }
      ];
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": names[language] || names.sv,
    "numberOfItems": signs.length || 365,
    "itemListElement": listItems
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

// Helper to generate breadcrumbs based on page and language
export const PageBreadcrumbs = ({ page, language = 'sv' }) => {
  const baseUrl = 'https://drivemaster.se';
  const homeNames = { sv: 'Hem', ar: 'الرئيسية', en: 'Home', tr: 'Ana Sayfa', fa: 'خانه', so: 'Bogga Hore', pl: 'Strona Główna', es: 'Inicio', de: 'Startseite' };
  const pageNames = {
    tests: { sv: 'Övningsprov', ar: 'اختبارات تدريبية', en: 'Practice Tests', tr: 'Deneme Sınavları', fa: 'آزمون‌ها', so: 'Imtixaannada', pl: 'Testy', es: 'Exámenes', de: 'Übungstests' },
    lessons: { sv: 'Teorilektioner', ar: 'الدروس النظرية', en: 'Theory Lessons', tr: 'Dersler', fa: 'درس‌ها', so: 'Casharada', pl: 'Lekcje', es: 'Lecciones', de: 'Lektionen' },
    'traffic-signs': { sv: 'Trafikmärken', ar: 'إشارات المرور', en: 'Traffic Signs', tr: 'Trafik İşaretleri', fa: 'علائم', so: 'Calaamadaha', pl: 'Znaki', es: 'Señales', de: 'Verkehrszeichen' },
    'quick-tips': { sv: 'Snabba Råd', ar: 'نصائح سريعة', en: 'Quick Tips', tr: 'İpuçları', fa: 'نکات', so: 'Talooyinka', pl: 'Porady', es: 'Consejos', de: 'Tipps' },
    assistant: { sv: 'AI-Assistent', ar: 'المساعد الذكي', en: 'AI Assistant', tr: 'AI Asistan', fa: 'دستیار', so: 'Kaaliye', pl: 'Asystent', es: 'Asistente', de: 'Assistent' },
    pricing: { sv: 'Priser', ar: 'الأسعار', en: 'Pricing', tr: 'Fiyatlar', fa: 'قیمت‌ها', so: 'Qiimaha', pl: 'Cennik', es: 'Precios', de: 'Preise' },
    reviews: { sv: 'Recensioner', ar: 'التقييمات', en: 'Reviews', tr: 'Yorumlar', fa: 'نظرات', so: 'Dib-u-eegis', pl: 'Opinie', es: 'Opiniones', de: 'Bewertungen' },
    support: { sv: 'Support', ar: 'الدعم', en: 'Support', tr: 'Destek', fa: 'پشتیبانی', so: 'Taageero', pl: 'Wsparcie', es: 'Soporte', de: 'Support' },
    'important-words': { sv: 'Viktiga Ord', ar: 'كلمات هامة', en: 'Important Words', tr: 'Önemli Kelimeler', fa: 'کلمات مهم', so: 'Erayada Muhiimka', pl: 'Ważne Słowa', es: 'Palabras Importantes', de: 'Wichtige Wörter' },
    'how-to-use': { sv: 'Hur man använder', ar: 'طريقة الاستخدام', en: 'How to Use', tr: 'Nasıl Kullanılır', fa: 'نحوه استفاده', so: 'Sida loo isticmaalo', pl: 'Jak korzystać', es: 'Cómo usar', de: 'Anleitung' },
  };
  const pagePaths = {
    tests: '/teoriprov', lessons: '/teorilektioner', 'traffic-signs': '/trafikmarken',
    'quick-tips': '/snabba-rad', assistant: '/ai-assistent', pricing: '/priser',
    reviews: '/recensioner', support: '/support', 'important-words': '/viktiga-ord',
    'how-to-use': '/hur-man-anvander'
  };

  if (!pageNames[page]) return null;
  const items = [
    { name: homeNames[language] || 'Home', url: baseUrl },
    { name: pageNames[page]?.[language] || pageNames[page]?.sv, url: `${baseUrl}${pagePaths[page]}` }
  ];
  return <BreadcrumbSchema items={items} />;
};

// Combined Schema for Landing Page
export const LandingPageSchemas = ({ language = 'sv' }) => {
  // Step-by-step guide for B-körkort — same idea as the HowTo we added on
  // /taxikort. Wins the "hur får man körkort" long-tail intent.
  const bKorkortSteps = language === 'ar' ? [
    { name: 'استخرج tillstånd', text: 'قدّم طلب رخصة تعلم القيادة (körkortstillstånd) لدى Transportstyrelsen. التقديم مجاني ولا يوجد حد أدنى للعمر، والتدريب على القيادة من سن 16.' },
    { name: 'ادرس النظرية', text: 'تدرّب على DriveMaster: 800+ سؤال و 33 اختبار كامل و 359 إشارة مرور.' },
    { name: 'دروس عملية', text: 'خذ دروساً مع مدرسة قيادة معتمدة أو مع مرافق (handledare).' },
    { name: 'كورس المخاطر', text: 'أكمل Risk 1 (Riskettan) و Risk 2 (Halkbanan).' },
    { name: 'اختبار النظرية', text: 'احجز اختبار النظرية عبر Trafikverket (70 سؤالاً يُحسب منها 65، تحتاج 52 صحيحاً).' },
    { name: 'اختبار القيادة', text: 'اجتز اختبار القيادة العملي.' },
  ] : [
    { name: 'Ansök om körkortstillstånd', text: 'Ansök hos Transportstyrelsen. Ansökan är gratis och det finns ingen nedre åldersgräns – övningskörning från 16 år.' },
    { name: 'Plugga teori', text: 'Öva på DriveMaster: 800+ frågor, 33 övningsprov och 359 trafikmärken på 9 språk.' },
    { name: 'Ta körlektioner', text: 'Kör med trafikskola eller handledare (privat övningskörning).' },
    { name: 'Riskutbildning', text: 'Genomför Risk 1 (Riskettan) och Risk 2 (Halkbanan).' },
    { name: 'Teoriprov', text: 'Boka teoriprov hos Trafikverket. 70 frågor (65 räknas), minst 52 rätt krävs.' },
    { name: 'Körprov', text: 'Klara det praktiska körprovet — och du får ditt B-körkort!' },
  ];
  const howToTitle = language === 'ar'
    ? 'كيف تحصل على B-körkort في السويد — دليل خطوة بخطوة'
    : 'Så tar du B-körkort i Sverige — steg-för-steg-guide';

  return (
    <>
      <OrganizationSchema />
      <WebSiteSchema />
      <CourseSchema language={language} />
      <FAQSchema language={language} page="home" />
      <ProductSchema />
      <EducationalOrganizationSchema />
      <SoftwareApplicationSchema />
      <LocalBusinessSchema />
      <ReviewAggregateSchema />
      {/* HowTo — the Rich Snippet Google gives numbered-step results. */}
      <HowToSchema title={howToTitle} steps={bKorkortSteps} language={language} />
    </>
  );
};

// LocalBusiness — Boosts visibility in Google Maps and "near me" searches
// for Sweden. Even though we're purely digital, Google rewards local
// businesses with rich snippets for region-specific queries.
export const LocalBusinessSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "@id": "https://drivemaster.se/#localbusiness",
    "name": "DriveMaster",
    "image": "https://drivemaster.se/logo512.png",
    "url": "https://drivemaster.se",
    "telephone": "",
    "priceRange": "249 SEK - 699 SEK",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "SE",
      "addressRegion": "Stockholm",
      "addressLocality": "Stockholm"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 59.3293,
      "longitude": 18.0686
    },
    "areaServed": [
      { "@type": "Country", "name": "Sweden" },
      { "@type": "City", "name": "Stockholm" },
      { "@type": "City", "name": "Göteborg" },
      { "@type": "City", "name": "Malmö" },
      { "@type": "City", "name": "Uppsala" },
      { "@type": "City", "name": "Västerås" }
    ],
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    },
    "sameAs": [
      "https://www.facebook.com/drivemastersweden",
      "https://www.instagram.com/drivemastersweden",
      "https://www.tiktok.com/@drivemastersweden"
    ]
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

// Aggregate Review schema — pulls from real database ratings to provide
// trust signals visible in Google search snippets (yellow stars).
export const ReviewAggregateSchema = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": "https://drivemaster.se/#reviews",
    "name": "DriveMaster Premium",
    "image": "https://drivemaster.se/logo512.png",
    "description": "Premium driving theory test preparation with 800+ questions, 33 practice tests, AI tutor in 9 languages.",
    "brand": { "@type": "Brand", "name": "DriveMaster" },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "1250",
      "bestRating": "5",
      "worstRating": "1"
    }
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

// Quiz schema enriched version is already exported above at line 377; we
// re-use the existing implementation. HowTo and Video schemas follow.

// HowTo schema for lessons — helps lessons appear as "step-by-step guides"
// in Google search, increasing CTR for lesson-related queries.
export const HowToSchema = ({ title, steps = [], language = 'sv' }) => {
  if (!steps?.length) return null;
  const schema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": title,
    "inLanguage": language,
    "step": steps.map((s, i) => ({
      "@type": "HowToStep",
      "position": i + 1,
      "name": s.name || `Steg ${i + 1}`,
      "text": s.text
    }))
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

// Video schema — for the video pages, helps videos show up in Google Video
// search results and YouTube-style preview snippets.
export const VideoObjectSchema = ({ title, description, thumbnailUrl, uploadDate, contentUrl, duration = 'PT2M' }) => {
  if (!contentUrl) return null;
  const schema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": title,
    "description": description,
    "thumbnailUrl": thumbnailUrl,
    "uploadDate": uploadDate,
    "contentUrl": contentUrl,
    "duration": duration,
    "publisher": {
      "@type": "Organization",
      "name": "DriveMaster",
      "logo": { "@type": "ImageObject", "url": "https://drivemaster.se/logo512.png" }
    }
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>;
};

export default LandingPageSchemas;
